import {
  FC,
  useState,
  useMemo,
  useEffect,
  useContext,
  Fragment,
  useRef,
} from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import axios from "axios";
import { AccountContext, AuthSession } from "auth/accountProvider";
import { LpiFilters, LpiOrder, LpiPagination, ShipmentLpi } from "./types";
import { formatNumber } from "../../utils/formatNumber";
import "./LpiResultsTable.scss";

interface LpiResultsTableProps {
  orders: LpiOrder[];
  filters?: LpiFilters;
  pagination?: LpiPagination | null;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLoading?: boolean;
}

type SortDirection = "asc" | "desc" | null;
type SortField = keyof LpiOrder | null;

type ShipmentDetailsRowProps = {
  order: LpiOrder;
  isLoading: boolean;
  error: string;
  shipments: ShipmentLpi[];
  columnsLength: number;
  t: TFunction;
};

const ShipmentDetailsRow: FC<ShipmentDetailsRowProps> = ({
  order,
  isLoading,
  error,
  shipments,
  columnsLength,
  t,
}) => {
  const safeShipments = Array.isArray(shipments) ? shipments : [];

  if (isLoading) {
    return (
      <tr className="lpi-results-table__expanded-row">
        <td colSpan={columnsLength + 1} className="lpi-results-table__expanded-cell">
          <div className="lpi-results-table__loading">
            <span className="spinner spinner-sm" />
            {t("Loading shipments...")}
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr className="lpi-results-table__expanded-row">
        <td colSpan={columnsLength + 1} className="lpi-results-table__expanded-cell">
          <div className="lpi-results-table__error">
            <span className="lpi-results-table__error-icon">⚠️</span>
            {error}
          </div>
        </td>
      </tr>
    );
  }

  if (safeShipments.length === 0) {
    return (
      <tr className="lpi-results-table__expanded-row">
        <td colSpan={columnsLength + 1} className="lpi-results-table__expanded-cell">
          <div className="lpi-results-table__no-shipments">
            {t("No shipments found for this transport order")}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="lpi-results-table__expanded-row">
      <td colSpan={columnsLength + 1} className="lpi-results-table__expanded-cell">
        <div className="lpi-results-table__shipments-wrapper">
          <h4 className="lpi-results-table__shipments-title">
            {t("Shipments for")} {order.transport_order_number}
          </h4>
          <table className="lpi-results-table__shipments-table">
            <thead>
              <tr>
                <th>{t("Order Number")}</th>
                <th>{t("Client ID")}</th>
                <th>{t("Volgnummer")}</th>
                <th>{t("Vehicle ID")}</th>
                <th>{t("Weight (tons)")}</th>
                <th>{t("GCD (km)")}</th>
                <th>{t("Distance (km)")}</th>
                <th>{t("Ton-km")}</th>
                <th>{t("LPI")}</th>
                <th>{t("Benuttingsgraad")}</th>
              </tr>
            </thead>
            <tbody>
              {safeShipments.map((shipment, index) => {
                const shipmentKey =
                  shipment.order_number ??
                  `${shipment.client_id ?? "client"}-${
                    shipment.volgnummer ?? `idx-${index}`
                  }`;
                return (
                  <tr key={shipmentKey} className="lpi-results-table__shipment-row">
                    <td>{shipment.order_number || "-"}</td>
                    <td>{shipment.client_id || "-"}</td>
                    <td>{shipment.volgnummer ?? "-"}</td>
                    <td>{shipment.vehicle_id || "-"}</td>
                    <td>
                      {formatNumber(shipment.weight_tons, 3)}
                    </td>
                    <td>
                      {formatNumber(shipment.gcd_km, 2)}
                    </td>
                    <td>
                      {formatNumber(shipment.add_km, 2)}
                    </td>
                    <td>
                      {formatNumber(shipment.beladingsprestatie_tonkm, 6)}
                    </td>
                    <td>
                      {formatNumber(shipment.lpi_value, 6)}
                    </td>
                    <td>
                      {shipment.benuttingsgraad === null ||
                      shipment.benuttingsgraad === undefined
                        ? "-"
                        : `${formatNumber(shipment.benuttingsgraad, 2)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
};

// Debounce delay in milliseconds
const SEARCH_DEBOUNCE_MS = 300;

// Human-readable labels for the reason codes the API attaches to an order when
// its LPI cannot be calculated. Anything not mapped falls back to the raw code.
const LPI_ERROR_LABEL_KEYS: Record<string, string> = {
  missing_add_data: "Missing ADD (distance driven) data",
  missing_beladingsprestatie: "Missing load performance (ton-km) data",
  missing_max_capacity: "Missing vehicle max capacity",
};

const LpiResultsTable: FC<LpiResultsTableProps> = ({
  orders,
  filters,
  pagination,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { getSession } = useContext(AccountContext);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Expandable row state
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [shipmentData, setShipmentData] = useState<
    Record<string, ShipmentLpi[]>
  >({});
  const [loadingShipments, setLoadingShipments] = useState<
    Record<string, boolean>
  >({});
  const [shipmentsError, setShipmentsError] = useState<Record<string, string>>(
    {}
  );

  // Ref to store current abort controller
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce search term to prevent UI freezes with large datasets
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Sorting logic
  const sortedOrders = useMemo(() => {
    if (!sortField || !sortDirection) return orders;

    return [...orders].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [orders, sortField, sortDirection]);

  // Filtering logic (uses debounced search term for filtering within current page)
  const filteredOrders = useMemo(() => {
    if (!debouncedSearchTerm) return sortedOrders;

    const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
    return sortedOrders.filter(
      (order) =>
        (order.transport_order_number || '').toLowerCase().includes(lowerSearchTerm) ||
        (order.clients && order.clients.some(client => (client || '').toLowerCase().includes(lowerSearchTerm))) ||
        (order.execution_date || '').toLowerCase().includes(lowerSearchTerm) ||
        (order.num_shipments ?? '').toString().includes(lowerSearchTerm) ||
        (order.lpi_value ?? '').toString().includes(lowerSearchTerm)
    );
  }, [sortedOrders, debouncedSearchTerm]);

  // Server-side pagination - use pagination data from API response
  const totalPages = pagination?.total_pages ?? 1;
  const totalCount = pagination?.total_count ?? orders.length;

  // Reset expanded row when a new result set arrives (e.g., filters/page change)
  useEffect(() => {
    setExpandedRow(null);
  }, [orders]);

  // Close expanded row when search term changes
  useEffect(() => {
    setExpandedRow(null);
  }, [debouncedSearchTerm]);

  // Clear shipment cache and collapse expanded rows when filters change so data refreshes per filter set
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setShipmentData({});
    setLoadingShipments({});
    setShipmentsError({});
    setExpandedRow(null);
  }, [filters]);

  // Cleanup: abort pending requests on unmount
  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    []
  );

  const handleSort = (field: keyof LpiOrder) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: keyof LpiOrder) => {
    if (sortField !== field) return "⇅";
    if (sortDirection === "asc") return "↑";
    if (sortDirection === "desc") return "↓";
    return "⇅";
  };

  const buildShipmentQueryString = () => {
    const params = new URLSearchParams();

    filters?.client_id?.forEach((id) => params.append("client_id", id));
    filters?.vehicle_id?.forEach((id) => params.append("vehicle_id", id));
    filters?.vehicle_type?.forEach((type) =>
      params.append("vehicle_type", type)
    );
    filters?.modality_detail?.forEach((detail) =>
      params.append("modality_detail", detail)
    );
    filters?.carrier_id?.forEach((id) => params.append("carrier_id", id));

    return params.toString();
  };

  const fetchShipments = async (
    transportOrderNumber: string,
    signal?: AbortSignal
  ): Promise<ShipmentLpi[]> => {
    const session: AuthSession = await getSession();
    const jwtToken = session.getAccessToken().getJwtToken();

    const queryString = buildShipmentQueryString();
    const response = await axios.get(
      `${process.env.REACT_APP_LPI_API_URL}/lpi/shipments/${transportOrderNumber}${queryString ? `?${queryString}` : ""
      }`,
      {
        headers: {
          Authorization: `Session ${jwtToken}`,
          "X-Session-Id": session.getIdToken().payload.sessionId,
        },
        signal,
      }
    );

    // Handle different response formats
    const { data } = response;

    // If response is already an array, return it
    if (Array.isArray(data)) {
      return data;
    }

    // If response has a shipments property, return that
    if (data && Array.isArray(data.shipments)) {
      return data.shipments;
    }

    // Return empty array as fallback
    return [];
  };

  const handleRowExpand = async (transportOrderNumber: string) => {
    // Toggle: if clicking on already expanded row, collapse it
    if (expandedRow === transportOrderNumber) {
      setExpandedRow(null);
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    // Cancel any previous pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Expand new row (auto-collapses previous due to single state)
    setExpandedRow(transportOrderNumber);

    // Use cached data if available
    if (shipmentData[transportOrderNumber]) {
      return;
    }

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Fetch shipment data
    setLoadingShipments((prev) => ({ ...prev, [transportOrderNumber]: true }));
    setShipmentsError((prev) => ({ ...prev, [transportOrderNumber]: "" }));

    try {
      const shipments = await fetchShipments(
        transportOrderNumber,
        abortController.signal
      );
      setShipmentData((prev) => ({
        ...prev,
        [transportOrderNumber]: shipments,
      }));
    } catch (error: any) {
      // Don't update state if request was aborted
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      const errorMessage =
        error.response?.data?.message || t("Failed to load shipments");
      setShipmentsError((prev) => ({
        ...prev,
        [transportOrderNumber]: errorMessage,
      }));
    } finally {
      // Don't update state if request was aborted
      if (!abortController.signal.aborted) {
        setLoadingShipments((prev) => ({
          ...prev,
          [transportOrderNumber]: false,
        }));
      }
      // Clear the ref if this is still the current controller
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd-MM-yyyy");
    } catch {
      return dateString;
    }
  };

  // Turn API reason codes into a readable, translated explanation. Returns an
  // empty string when there are no reasons, so callers can conditionally render.
  const describeLpiErrors = (errors?: string[]): string =>
    (errors ?? [])
      .map((code) => t(LPI_ERROR_LABEL_KEYS[code] ?? code))
      .join("; ");

  const columns = [
    {
      key: "transport_order_number" as keyof LpiOrder,
      label: t("Transport Order Number"),
      render: (order: LpiOrder) => order.transport_order_number || "-",
    },
    {
      key: "client_id" as keyof LpiOrder,
      label: t("Client"),
      render: (order: LpiOrder) => {
        if (order.clients && order.clients.length > 0) {
          return order.clients.join(", ");
        }
        return order.client_id || "-";
      },
    },
    {
      key: "execution_date" as keyof LpiOrder,
      label: t("Execution Date"),
      render: (order: LpiOrder) => formatDate(order.execution_date || ""),
    },
    {
      key: "num_shipments" as keyof LpiOrder,
      label: t("Shipments"),
      render: (order: LpiOrder) => order.num_shipments ?? "-",
    },
    {
      key: "total_beladingsprestatie_tonkm" as keyof LpiOrder,
      label: t("Total ton-km"),
      render: (order: LpiOrder) =>
        formatNumber(order.total_beladingsprestatie_tonkm, 6),
    },
    {
      key: "total_add_km" as keyof LpiOrder,
      label: t("Total km"),
      render: (order: LpiOrder) =>
        formatNumber(order.total_add_km, 2),
    },
    {
      key: "benuttingsgraad" as keyof LpiOrder,
      label: t("Benuttingsgraad"),
      render: (order: LpiOrder) => {
        if (order.benuttingsgraad === null || order.benuttingsgraad === undefined) {
          return "-";
        }
        return `${formatNumber(order.benuttingsgraad, 2)}%`;
      },
    },
    {
      key: "lpi_value" as keyof LpiOrder,
      label: t("LPI Value"),
      render: (order: LpiOrder) => {
        if (order.lpi_value !== null && order.lpi_value !== undefined) {
          return formatNumber(order.lpi_value, 6);
        }
        // LPI could not be computed. Show the reason only when the API tells us why.
        const reason = describeLpiErrors(order.errors);
        return (
          <span className="lpi-results-table__no-lpi">
            {formatNumber(null)}
            {reason && (
              <span
                className="lpi-results-table__reason"
                title={reason}
                aria-label={reason}
              >
                ⚠
              </span>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div className="lpi-results-table">
      <div className="lpi-results-table__header">
        <h3>{t("Transport Orders LPI Results")}</h3>
        <div className="lpi-results-table__controls">
          <input
            type="text"
            className="lpi-results-table__search"
            placeholder={t("Search...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label={t("Search")}
          />
          <select
            className="lpi-results-table__page-size"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label={t("Items per page")}
            disabled={isLoading}
          >
            <option value={10}>10 {t("per page")}</option>
            <option value={25}>25 {t("per page")}</option>
            <option value={50}>50 {t("per page")}</option>
            <option value={100}>100 {t("per page")}</option>
          </select>
        </div>
      </div>

      <div className="lpi-results-table__info">
        {isLoading ? (
          <span className="lpi-results-table__loading-indicator">
            <span className="spinner spinner-sm" /> {t("Loading...")}
          </span>
        ) : (
          <>
            {t("Showing")}{" "}
            {orders.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}{" "}
            {t("to")} {Math.min(currentPage * pageSize, totalCount)}{" "}
            {t("of")} {totalCount} {t("entries")}
            {debouncedSearchTerm &&
              ` (${t("filtering")} ${filteredOrders.length} ${t("on current page")})`}
            {sortField && sortDirection && totalPages > 1 && (
              <span className="lpi-results-table__sort-note">
                {" "}
                · {t("sorted on current page only")}
              </span>
            )}
          </>
        )}
      </div>

      <div className="lpi-results-table__wrapper">
        <table className="lpi-results-table__table">
          <thead>
            <tr>
              {/* Empty header cell for expand/collapse column */}
              <th
                className="lpi-results-table__expand-header"
                aria-label={t("Expand or collapse row")}
              />

              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => handleSort(column.key)}
                  className="lpi-results-table__header-cell"
                >
                  {column.label}{" "}
                  <span className="lpi-results-table__sort-icon">
                    {getSortIcon(column.key)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => {
                const isExpanded = expandedRow === order.transport_order_number;
                const isLoadingShipments =
                  loadingShipments[order.transport_order_number] || false;
                const shipmentError =
                  shipmentsError[order.transport_order_number] || "";
                const shipments =
                  shipmentData[order.transport_order_number] || [];

                return (
                  <Fragment key={order.transport_order_number}>
                    <tr
                      className={`lpi-results-table__row ${
                        isExpanded ? "lpi-results-table__row--expanded" : ""
                      }`}
                    >
                      {/* Expand/collapse icon cell */}
                      <td className="lpi-results-table__cell lpi-results-table__expand-cell">
                        <button
                          className="lpi-results-table__expand-button"
                          type="button"
                          onClick={() =>
                            handleRowExpand(order.transport_order_number)
                          }
                          aria-label={isExpanded ? t("Collapse") : t("Expand")}
                          aria-expanded={isExpanded}
                        >
                          <svg
                            className={`lpi-results-table__expand-icon ${
                              isExpanded
                                ? "lpi-results-table__expand-icon--expanded"
                                : ""
                            }`}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                          </svg>
                        </button>
                      </td>

                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="lpi-results-table__cell"
                        >
                          {column.render(order)}
                        </td>
                      ))}
                    </tr>

                    {isExpanded && (
                      <ShipmentDetailsRow
                        order={order}
                        isLoading={isLoadingShipments}
                        error={shipmentError}
                        shipments={shipments}
                        columnsLength={columns.length}
                        t={t}
                      />
                    )}
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 1} // +1 for expand column
                  className="lpi-results-table__no-data"
                >
                  {t("No data available")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="lpi-results-table__pagination">
          <button
            className="lpi-results-table__pagination-btn"
            type="button"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1 || isLoading}
          >
            «
          </button>
          <button
            className="lpi-results-table__pagination-btn"
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || isLoading}
          >
            ‹
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (page) =>
                // Show first page, last page, current page, and pages around current
                page === 1 ||
                page === totalPages ||
                Math.abs(page - currentPage) <= 1
            )
            .map((page, index, array) => {
              // Add ellipsis if there's a gap
              const prevPage = array[index - 1];
              const showEllipsis = prevPage && page - prevPage > 1;

              return (
                <span key={page}>
                  {showEllipsis && (
                    <span className="lpi-results-table__pagination-ellipsis">
                      ...
                    </span>
                  )}
                  <button
                    className={`lpi-results-table__pagination-btn ${currentPage === page
                        ? "lpi-results-table__pagination-btn--active"
                        : ""
                      }`}
                    type="button"
                    onClick={() => onPageChange(page)}
                    disabled={isLoading}
                  >
                    {page}
                  </button>
                </span>
              );
            })}

          <button
            className="lpi-results-table__pagination-btn"
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || isLoading}
          >
            ›
          </button>
          <button
            className="lpi-results-table__pagination-btn"
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages || isLoading}
          >
            »
          </button>
        </div>
      )}
    </div>
  );
};

export default LpiResultsTable;
